import { useState, useEffect, useMemo } from "react";
import { X, Video } from "lucide-react";
import { Link } from "react-router-dom";

/** Return the first Saturday of a given month/year */
function getFirstSaturday(year: number, month: number): Date {
  const d = new Date(year, month, 1);
  const day = d.getDay();
  const offset = (6 - day + 7) % 7;
  d.setDate(1 + offset);
  return d;
}

/**
 * Check if we're in the prayer conference reminder window.
 *
 * Normal case (first Saturday falls on 4th–7th):
 *   Window = 1st of the month → first Saturday 10:00 AM PST
 *
 * Early case (first Saturday falls on 1st–3rd):
 *   The reminder window is too short, so we start from the last week
 *   of the previous month (7 days before the 1st) through the first
 *   Saturday at 10:00 AM PST.
 *
 * Examples:
 *   • First Sat = Jan 1 → window starts Dec 25 prev year
 *   • First Sat = Mar 2 → window starts Feb 22
 *   • First Sat = Jun 7 → window starts Jun 1 (normal)
 */
function isPrayerWeek(): boolean {
  const nowStr = new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
  const now = new Date(nowStr);

  const year = now.getFullYear();
  const month = now.getMonth();
  const firstSat = getFirstSaturday(year, month);
  const satDay = firstSat.getDate(); // 1–7

  // End of window: first Saturday at 10:00 AM PST
  const windowEnd = new Date(year, month, satDay, 10, 0, 0);

  // Start of window depends on how early the Saturday falls
  let windowStart: Date;
  if (satDay <= 3) {
    // Early Saturday — start 7 days before the 1st (last week of previous month)
    windowStart = new Date(year, month, 1 - 7, 0, 0, 0);
  } else {
    // Normal — start on the 1st
    windowStart = new Date(year, month, 1, 0, 0, 0);
  }

  // Check current month's window
  if (now >= windowStart && now <= windowEnd) {
    return true;
  }

  // Also check if we're in the early-start window for NEXT month's prayer conference
  const nextMonth = new Date(year, month + 1, 1);
  const nextFirstSat = getFirstSaturday(nextMonth.getFullYear(), nextMonth.getMonth());
  const nextSatDay = nextFirstSat.getDate();

  if (nextSatDay <= 3) {
    const nextWindowStart = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1 - 7, 0, 0, 0);
    const nextWindowEnd = new Date(
      nextMonth.getFullYear(), nextMonth.getMonth(), nextSatDay, 10, 0, 0
    );
    if (now >= nextWindowStart && now <= nextWindowEnd) {
      return true;
    }
  }

  return false;
}

/** Get the "next" first-Saturday prayer conference date */
function getNextPrayerDate(): Date {
  const now = new Date();
  const thisMonth = getFirstSaturday(now.getFullYear(), now.getMonth());
  if (thisMonth >= new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
    return thisMonth;
  }
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return getFirstSaturday(next.getFullYear(), next.getMonth());
}

const MONTH_COLORS: Record<number, { from: string; to: string; accent: string }> = {
  0: { from: "#1e3a5f", to: "#2563eb", accent: "#93c5fd" },
  1: { from: "#7c2d12", to: "#dc2626", accent: "#fca5a5" },
  2: { from: "#064e3b", to: "#059669", accent: "#6ee7b7" },
  3: { from: "#4a1d96", to: "#7c3aed", accent: "#c4b5fd" },
  4: { from: "#065f46", to: "#10b981", accent: "#a7f3d0" },
  5: { from: "#92400e", to: "#f59e0b", accent: "#fde68a" },
  6: { from: "#1e40af", to: "#3b82f6", accent: "#bfdbfe" },
  7: { from: "#7f1d1d", to: "#ef4444", accent: "#fecaca" },
  8: { from: "#78350f", to: "#d97706", accent: "#fcd34d" },
  9: { from: "#4c1d95", to: "#8b5cf6", accent: "#ddd6fe" },
  10: { from: "#064e3b", to: "#14b8a6", accent: "#99f6e4" },
  11: { from: "#1e3a5f", to: "#0ea5e9", accent: "#bae6fd" },
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const PrayerConferenceModal = ({ onDismissed }: { onDismissed: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);

  const nextDate = useMemo(() => getNextPrayerDate(), []);
  const month = nextDate.getMonth();
  const colors = MONTH_COLORS[month];
  const formattedDate = nextDate.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    // The shared "Meetings & Prayer" flash-card dialog on the homepage already
    // carries the monthly prayer card; never open a second dialog on top of it.
    onDismissed();
  }, [onDismissed]);

  const dismiss = () => {
    setIsOpen(false);
    onDismissed();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={dismiss}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div
          className="relative px-6 pt-8 pb-6 text-center text-white"
          style={{
            background: `linear-gradient(135deg, ${colors.from} 0%, ${colors.to} 100%)`,
          }}
        >
          <div
            className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10"
            style={{ background: colors.accent, transform: "translate(30%, -30%)" }}
          />
          <div
            className="absolute bottom-0 left-0 w-24 h-24 rounded-full opacity-10"
            style={{ background: colors.accent, transform: "translate(-30%, 30%)" }}
          />

          <p className="text-sm font-medium tracking-widest uppercase opacity-80 mb-2">
            OFUESI Family
          </p>
          <h2 className="text-2xl font-bold leading-tight mb-1">
            Hello! 🙏
          </h2>
          <p className="text-base opacity-90 mb-4">
            Please join us for our
          </p>

          <div
            className="rounded-xl p-4 mb-4"
            style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(4px)" }}
          >
            <h3 className="text-xl font-bold mb-1">Monthly Virtual</h3>
            <h3 className="text-xl font-bold mb-3">Prayer Conference</h3>
            <div className="w-12 h-0.5 mx-auto mb-3" style={{ background: colors.accent }} />
            <p className="text-lg font-semibold" style={{ color: colors.accent }}>
              {MONTH_NAMES[month]} {nextDate.getFullYear()}
            </p>
          </div>

          <div className="space-y-1 text-sm">
            <p className="font-semibold text-base">{formattedDate}</p>
            <p className="opacity-90">10:30 AM – 11:30 AM EST</p>
            <p className="opacity-75 text-xs mt-1">1 hour of fellowship & prayer</p>
          </div>

          <div className="mt-4 flex items-center justify-center gap-2 text-xs opacity-80">
            <Video className="h-4 w-4" />
            <span>Via Zoom</span>
          </div>
        </div>

        <div
          className="px-4 py-3 text-center"
          style={{ background: colors.accent }}
        >
          <Link
            to="/prayer"
            onClick={dismiss}
            className="font-semibold text-sm hover:underline"
            style={{ color: colors.from }}
          >
            View Details & Join →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrayerConferenceModal;
